import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Alert,
  Spinner
} from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import classnames from "classnames";
import axios from 'axios'; // Axios import edildi
import toastr from 'toastr'; 
import 'toastr/build/toastr.min.css'; 

// --- API YAPILANDIRMASI ---
const API_URL = "https://dlaviapi.stechomeyazilim.info/App_Page_Content";

// Kategori Başlıkları Sözlüğü
const PAGE_CODE_LABELS = {
  "WHY_DLAVIA": "Neden DLAvia?",
  "PRIVACY": "Gizlilik Politikası",
  "EXAM_INFO": "Sınav Bilgileri",
  "CONTACT": "İletişim",
  "CONTACT_NOTE": "Dipnotlar",
  "INVITE": "Davet Alanı"
};

const PageContentManager = () => {
  document.title = "Uygulama İçerik Yönetimi | DLApro Paneli";

  // State Yönetimi
  const [localContent, setLocalContent] = useState([]);
  const [activeTab, setActiveTab] = useState("WHY_DLAVIA");
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Yeni konu ekleme formu (aktif sekme için). Sıra no'yu kullanıcı belirler.
  const emptyNewItem = { Sort_Order: "", Title_TR: "", Title_EN: "", Description_TR: "", Description_EN: "" };
  const [newItem, setNewItem] = useState(emptyNewItem);
  const [isCreating, setIsCreating] = useState(false);

  // --- VERİ ÇEKME (FETCH) ---
  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(API_URL);
      
      // API dönüş yapısına göre veriyi yakalama
      // Olası yapılar: res.data, res.data.value, veya res.data.App_Page_Content
      let data = [];
      if (response.data && Array.isArray(response.data)) {
          data = response.data;
      } else if (response.data?.value && Array.isArray(response.data.value)) {
          data = response.data.value; // OData formatı
      } else if (response.data?.App_Page_Content && Array.isArray(response.data.App_Page_Content)) {
          data = response.data.App_Page_Content; // Özel JSON formatı
      }

      // Sıralama (Sort_Order'a göre)
      const sortedList = data.sort((a, b) => a.Sort_Order - b.Sort_Order);
      setLocalContent(sortedList);

      // İlk tabı otomatik seç (eğer veri varsa ve mevcut tab boşsa)
      if (sortedList.length > 0 && !activeTab) {
          setActiveTab(sortedList[0].Page_Code);
      }

    } catch (error) {
      console.error("Veri çekme hatası:", error);
      showToast(false, "Veriler sunucudan çekilemedi.");
    } finally {
      setIsLoading(false);
    }
  };

  // --- GÜNCELLEME (UPDATE) ---
  const handleUpdateItem = async (item) => {
    setIsUpdating(true);

    // ID URL'de verildiği için payload'tan çıkarılır.
    // OData V4'te var olan kaydı güncellemek için PUT/PATCH kullanılır.
    // Back-end'de PATCH action'ı ekleme (Add) yapıyorsa PUT denenir.
    const { ID, ...payload } = {
      ID: item.ID,
      Page_Code: item.Page_Code,
      Title_TR: item.Title_TR,
      Title_EN: item.Title_EN,
      Description_TR: item.Description_TR,
      Description_EN: item.Description_EN,
      Image_Url: item.Image_Url,
      Sort_Order: item.Sort_Order
    };

    try {
      await axios.put(`${API_URL}(${item.ID})`, payload);

      showToast(true, "Kayıt başarıyla güncellendi.");
      fetchContent();
    } catch (error) {
      console.error("Güncelleme hatası:", error);
      showToast(false, "Güncelleme sırasında hata oluştu.");
    } finally {
      setIsUpdating(false);
    }
  };

  // --- SİLME (DELETE) ---
  const handleDeleteItem = async (id) => {
    if (!window.confirm("Bu kaydı silmek istediğinize emin misiniz?")) return;

    setDeletingId(id);
    try {
      await axios.delete(`${API_URL}(${id})`);
      showToast(true, "Kayıt başarıyla silindi.");
      fetchContent();
    } catch (error) {
      console.error("Silme hatası:", error);
      showToast(false, "Silme sırasında hata oluştu.");
    } finally {
      setDeletingId(null);
    }
  };

  // --- YENİ KONU EKLEME (CREATE) ---
  // Sıra no boş bırakılırsa o kategorideki en yüksek sıra + 1 önerilir.
  const nextSortOrder = (pageCode) => {
    const orders = localContent
      .filter(item => item.Page_Code === pageCode)
      .map(item => Number(item.Sort_Order) || 0);
    return orders.length > 0 ? Math.max(...orders) + 1 : 1;
  };

  const handleCreateItem = async (pageCode) => {
    if (!newItem.Title_TR?.trim() && !newItem.Title_EN?.trim()) {
      showToast(false, "Lütfen en az bir başlık (TR veya EN) girin.");
      return;
    }

    const sortOrder = String(newItem.Sort_Order).trim() !== ""
      ? Number(newItem.Sort_Order)
      : nextSortOrder(pageCode);

    setIsCreating(true);
    const payload = {
      Page_Code: pageCode,
      Title_TR: newItem.Title_TR,
      Title_EN: newItem.Title_EN,
      Description_TR: newItem.Description_TR,
      Description_EN: newItem.Description_EN,
      Image_Url: "",
      Sort_Order: sortOrder
    };

    try {
      await axios.post(API_URL, payload);
      showToast(true, "Yeni konu başarıyla eklendi.");
      setNewItem(emptyNewItem);
      fetchContent();
    } catch (error) {
      console.error("Ekleme hatası:", error);
      showToast(false, "Ekleme sırasında hata oluştu.");
    } finally {
      setIsCreating(false);
    }
  };

  const handleNewItemChange = (field, value) => {
    setNewItem(prev => ({ ...prev, [field]: value }));
  };

  // Input Değişikliklerini Yönetme (Local State)
  const handleInputChange = (id, field, value) => {
    setLocalContent(prevList =>
      prevList.map(item =>
        item.ID === id ? { ...item, [field]: value } : item
      )
    );
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      setNewItem(emptyNewItem); // Sekme değişince ekleme formunu temizle
    }
  };

  const showToast = (isSuccess, msg) => {
    toastr.options = { positionClass: "toast-top-right", timeOut: 3000 };
    if (isSuccess) toastr.success(msg, "Başarılı");
    else toastr.error(msg, "Hata");
  };

  // Sekme listesi: tanımlı tüm kategoriler HER ZAMAN gösterilir, üzerine
  // veride bulunan ekstra kodlar eklenir. Böylece bir kategorinin tüm satırları
  // silinse bile sekmesi kaybolmaz ve yeni konu eklenebilir (müşteri talebi).
  const availableTabs = [
    ...new Set([...Object.keys(PAGE_CODE_LABELS), ...localContent.map(item => item.Page_Code)])
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Yönetim" breadcrumbItem="Uygulama İçerik Yönetimi" />

          <Row>
            <Col xs="12">
              <Card>
                <CardBody>
                  
                  {isLoading ? (
                    <div className="text-center py-5">
                        <Spinner color="primary" />
                        <p className="mt-2">Veriler Yükleniyor...</p>
                    </div>
                  ) : (
                    <>
                      {/* SEKMELER */}
                      <Nav tabs className="nav-tabs-custom mb-4">
                        {availableTabs.map((code) => (
                          <NavItem key={code}>
                            <NavLink
                              style={{ cursor: "pointer" }}
                              className={classnames({ active: activeTab === code })}
                              onClick={() => toggleTab(code)}
                            >
                              {PAGE_CODE_LABELS[code] || code}
                            </NavLink>
                          </NavItem>
                        ))}
                      </Nav>

                      {/* İÇERİK */}
                      <TabContent activeTab={activeTab}>
                        {availableTabs.map((code) => (
                          <TabPane tabId={code} key={code}>
                            <div className="d-flex flex-column gap-4">
                              {localContent
                                .filter(item => item.Page_Code === code)
                                .map((item) => (
                                  <Card key={item.ID} className="border shadow-sm bg-light mb-0">
                                    <CardBody>
                                      <Row>
                                        <Col md={1} className="d-flex align-items-center justify-content-center bg-white border-end rounded-start">
                                            <h3 className="text-secondary mb-0">#{item.Sort_Order}</h3>
                                        </Col>
                                        <Col md={11}>
                                            <Row className="mb-3 mt-2">
                                                <Col md={6}>
                                                    <Label className="fw-bold text-primary">Başlık (TR)</Label>
                                                    <Input 
                                                        type="text" 
                                                        value={item.Title_TR || ""} 
                                                        onChange={(e) => handleInputChange(item.ID, 'Title_TR', e.target.value)}
                                                    />
                                                </Col>
                                                <Col md={6}>
                                                    <Label className="fw-bold text-success">Title (EN)</Label>
                                                    <Input 
                                                        type="text" 
                                                        value={item.Title_EN || ""} 
                                                        onChange={(e) => handleInputChange(item.ID, 'Title_EN', e.target.value)}
                                                    />
                                                </Col>
                                            </Row>
                                            <Row className="mb-3">
                                                <Col md={6}>
                                                    <Label>Açıklama (TR)</Label>
                                                    <Input 
                                                        type="textarea" 
                                                        rows="3"
                                                        value={item.Description_TR || ""} 
                                                        onChange={(e) => handleInputChange(item.ID, 'Description_TR', e.target.value)}
                                                    />
                                                </Col>
                                                <Col md={6}>
                                                    <Label>Description (EN)</Label>
                                                    <Input 
                                                        type="textarea" 
                                                        rows="3"
                                                        value={item.Description_EN || ""} 
                                                        onChange={(e) => handleInputChange(item.ID, 'Description_EN', e.target.value)}
                                                    />
                                                </Col>
                                            </Row>
                                            
                                            {/* Image URL alanı (Gerekirse açılabilir) */}
                                            {/* <Row className="mb-3">
                                                <Col md={12}>
                                                    <Label>Görsel URL</Label>
                                                    <Input 
                                                        type="text" 
                                                        value={item.Image_Url || ""} 
                                                        onChange={(e) => handleInputChange(item.ID, 'Image_Url', e.target.value)}
                                                    />
                                                </Col>
                                            </Row> 
                                            */}

                                            <div className="d-flex justify-content-end border-top pt-3 gap-2">
                                                <Button
                                                    color="danger"
                                                    outline
                                                    disabled={deletingId === item.ID}
                                                    onClick={() => handleDeleteItem(item.ID)}
                                                >
                                                    {deletingId === item.ID ? <Spinner size="sm"/> : <i className="mdi mdi-delete me-1"></i>}
                                                    Sil
                                                </Button>
                                                <Button
                                                    color="primary"
                                                    disabled={isUpdating}
                                                    onClick={() => handleUpdateItem(item)}
                                                >
                                                    {isUpdating ? <Spinner size="sm"/> : <i className="mdi mdi-content-save me-1"></i>}
                                                    Bu Kaydı Güncelle
                                                </Button>
                                            </div>
                                        </Col>
                                      </Row>
                                    </CardBody>
                                  </Card>
                                ))}
                            </div>
                            
                            {localContent.filter(item => item.Page_Code === code).length === 0 && (
                                <Alert color="warning" className="mt-3">Bu kategoride henüz içerik bulunamadı. Aşağıdan yeni konu ekleyebilirsiniz.</Alert>
                            )}

                            {/* --- YENİ KONU EKLE --- */}
                            <Card className="border border-primary border-dashed shadow-sm mt-4 mb-0">
                              <CardBody>
                                <div className="d-flex align-items-center mb-3">
                                  <i className="mdi mdi-plus-circle-outline text-primary fs-4 me-2"></i>
                                  <h5 className="text-primary mb-0">Yeni Konu Ekle</h5>
                                </div>
                                <Row className="mb-3">
                                  <Col md={2}>
                                    <Label className="fw-bold">Sıra No</Label>
                                    <Input
                                      type="number"
                                      min="1"
                                      placeholder={`${nextSortOrder(code)}`}
                                      value={newItem.Sort_Order}
                                      onChange={(e) => handleNewItemChange('Sort_Order', e.target.value)}
                                    />
                                    <small className="text-muted">Boş bırakılırsa: {nextSortOrder(code)}</small>
                                  </Col>
                                  <Col md={5}>
                                    <Label className="fw-bold text-primary">Başlık (TR)</Label>
                                    <Input
                                      type="text"
                                      value={newItem.Title_TR}
                                      onChange={(e) => handleNewItemChange('Title_TR', e.target.value)}
                                    />
                                  </Col>
                                  <Col md={5}>
                                    <Label className="fw-bold text-success">Title (EN)</Label>
                                    <Input
                                      type="text"
                                      value={newItem.Title_EN}
                                      onChange={(e) => handleNewItemChange('Title_EN', e.target.value)}
                                    />
                                  </Col>
                                </Row>
                                <Row className="mb-3">
                                  <Col md={6}>
                                    <Label>Açıklama (TR)</Label>
                                    <Input
                                      type="textarea"
                                      rows="3"
                                      value={newItem.Description_TR}
                                      onChange={(e) => handleNewItemChange('Description_TR', e.target.value)}
                                    />
                                  </Col>
                                  <Col md={6}>
                                    <Label>Description (EN)</Label>
                                    <Input
                                      type="textarea"
                                      rows="3"
                                      value={newItem.Description_EN}
                                      onChange={(e) => handleNewItemChange('Description_EN', e.target.value)}
                                    />
                                  </Col>
                                </Row>
                                <div className="d-flex justify-content-end border-top pt-3">
                                  <Button
                                    color="success"
                                    disabled={isCreating}
                                    onClick={() => handleCreateItem(code)}
                                  >
                                    {isCreating ? <Spinner size="sm"/> : <i className="mdi mdi-plus me-1"></i>}
                                    Yeni Konu Ekle
                                  </Button>
                                </div>
                              </CardBody>
                            </Card>

                          </TabPane>
                        ))}
                      </TabContent>
                    </>
                  )}

                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PageContentManager;